import { zonedDayStartIso } from '../../../../../function-utils/building-time';
import { GuestGuideBody, GuideOpenBody, GuideStage } from '../../../../../interfaces/stay-guide';

/**
 * THE GUIDE'S SCREEN, FROM THE SERVER'S ANSWER (plan S7.7) — pure, no React.
 *
 *   200 + a body  → its `state` (pin_required, pin_locked, upcoming, ended, open)
 *   404           → invalid (S6's invalid-link screen: one answer for every
 *                   non-live link, by design)
 *   429           → limited (no retry for the limiter's minute)
 *   anything else → error ("We could not load your guide." + Try again)
 *
 * A 200 whose body is not one of the five shapes is `error`, never a guess:
 * the page must not render an open guide out of half an answer.
 *
 * Table-driven by src/utils/stays-guide-states.cases.json (guide-state.test.ts).
 */

export type GuideScreen =
  | 'invalid'
  | 'limited'
  | 'error'
  | 'pin_required'
  | 'pin_locked'
  | 'upcoming'
  | 'ended'
  | 'open';

export type GuideSectionId = 'arrival' | 'essentials' | 'local' | 'checkout' | 'photos';

/** The five sections, in the nav's order. */
export const GUIDE_SECTIONS: readonly GuideSectionId[] = ['arrival', 'essentials', 'local', 'checkout', 'photos'];

export const isGuideSection = (value: unknown): value is GuideSectionId =>
  typeof value === 'string' && (GUIDE_SECTIONS as readonly string[]).includes(value);

/** What a load came back with: the body, or the refusal's status (0 = no answer). */
export type GuideOutcome = { ok: true; body: unknown } | { ok: false; status: number; code?: string | null };

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

const isOpenBody = (b: Record<string, unknown>): boolean =>
  typeof b.stage === 'string' &&
  typeof b.zone === 'string' &&
  isObject(b.window) &&
  isObject(b.stay) &&
  isObject(b.guide) &&
  isObject(b.media) &&
  Array.isArray(b.faqs) &&
  Array.isArray(b.tips) &&
  (b.access === null || isObject(b.access));

/** The body as a guide answer, or null when it is none of the five shapes. */
export const asGuideBody = (body: unknown): GuestGuideBody | null => {
  if (!isObject(body)) return null;
  switch (body.state) {
    case 'pin_required':
    case 'pin_locked':
      return isObject(body.pin) ? (body as unknown as GuestGuideBody) : null;
    case 'upcoming':
    case 'ended':
      return isObject(body.stay) ? (body as unknown as GuestGuideBody) : null;
    case 'open':
      return isOpenBody(body) ? (body as unknown as GuestGuideBody) : null;
    default:
      return null;
  }
};

export const screenOf = (outcome: GuideOutcome): GuideScreen => {
  if (!outcome.ok) {
    if (outcome.status === 404) return 'invalid';
    if (outcome.status === 429) return 'limited';

    return 'error';
  }
  const body = asGuideBody(outcome.body);

  return body ? body.state : 'error';
};

/** The section the hub marks "Now" (contract §4). */
export const nowSectionOf = (stage: GuideStage): GuideSectionId => {
  if (stage === 'during_stay') return 'essentials';
  if (stage === 'checkout_day' || stage === 'after_departure') return 'checkout';

  return 'arrival';
};

/** The sections this guide has: Local tips only with tips, Photos only with a gallery. */
export const sectionsShown = (body: GuideOpenBody): GuideSectionId[] =>
  GUIDE_SECTIONS.filter(id => {
    if (id === 'local') return body.tips.length > 0;
    if (id === 'photos') return body.media.gallery.length > 0;

    return true;
  });

/**
 * The instants at which this answer can change on its own: the guide opening,
 * the codes appearing, check-in, the start of the check-out DAY in the
 * building's zone (the stage turns to checkout_day), check-out, the window
 * closing — and, for a locked PIN, the end of the lock. Sorted, unique.
 */
export const boundariesOf = (body: GuestGuideBody): string[] => {
  const out: Array<string | null | undefined> = [];
  if (body.state === 'upcoming') out.push(body.opens_at);
  if (body.state === 'pin_locked') out.push(body.pin.locked_until);
  if (body.state === 'open') {
    const w = body.window;
    out.push(w.opens_at, w.reveal_at, w.check_in_at, w.check_out_at, w.closes_at);
    out.push(zonedDayStartIso(body.stay.check_out, body.zone));
    if (body.access_notice && body.access_notice.reveal_at) out.push(body.access_notice.reveal_at);
  }
  const ms = out
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .map(v => Date.parse(v))
    .filter(v => Number.isFinite(v));

  return Array.from(new Set(ms))
    .sort((a, b) => a - b)
    .map(v => new Date(v).toISOString());
};

/** The first boundary strictly after `nowMs`, in ms since the epoch; null when none is left. */
export const nextBoundaryMs = (body: GuestGuideBody, nowMs: number): number | null => {
  for (const iso of boundariesOf(body)) {
    const at = Date.parse(iso);
    if (at > nowMs) return at;
  }

  return null;
};

/**
 * When the shortest-lived photo URL of an open guide runs out (a sensitive
 * photo's presigned URL lives 300 s), in ms since `loadedAtMs`; null with no
 * photo. The page re-asks a little before, so an open page never shows a dead
 * image.
 */
export const photoExpiryMs = (body: GuestGuideBody, loadedAtMs: number): number | null => {
  if (body.state !== 'open') return null;
  const photos = [
    body.media.cover,
    body.media.host,
    body.media.step1,
    body.media.step2,
    ...body.media.gallery,
    ...body.tips.map(t => t.photo),
    ...(body.access ? Object.values(body.access.media) : []),
  ];
  const lives = photos
    .map(p => (p && typeof p.expires_in === 'number' && p.expires_in > 0 ? p.expires_in : null))
    .filter((v): v is number => v !== null);
  if (!lives.length) return null;

  return loadedAtMs + Math.min(...lives) * 1000;
};
